package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * The seat position, both sides of it.
 *
 * Two numbers rather than one, because the interesting fact is whether they agree. "You have 6 owner
 * and admin seats. Paddle is billing 6." is reassuring; the same sentence ending in 4 is a customer
 * being undercharged, and ending in 8 is one being overcharged. Either way they should hear it from
 * us before they hear it from an invoice.
 */
@Serializable
data class SeatStateDto(
    /** Owners and admins in the workspace right now. */
    val held: Int,
    /** What the processor says it is charging for. Null before the first webhook. */
    val billed: Int? = null,
    /**
     * A mismatch younger than a minute renders as "updating…", not as an error.
     *
     * A legitimate seat change takes a couple of seconds to reach Paddle and come back. Shouting
     * during that window would make the page cry wolf on every ordinary promotion, and a warning
     * that is usually wrong is a warning nobody reads when it is right.
     */
    val syncing: Boolean = false,
) {
    val matches: Boolean get() = billed == null || billed == held
}

/**
 * Where a failed payment is in its retry cycle.
 *
 * Every field is copied from a webhook payload and **never inferred**. A "past due since" we
 * calculated ourselves would be a guess presented as a fact, on the one screen where a customer is
 * deciding whether we can be trusted with their card. Null means Paddle has not told us, and the
 * page says nothing rather than something plausible.
 */
@Serializable
data class DunningDto(
    val pastDueSince: String? = null,
    val nextRetryAt: String? = null,
    /** When the subscription is cancelled for non-payment if nothing changes. */
    val endsAt: String? = null,
)

/** One invoice or refund, from our own cache rather than a live call to the processor. */
@Serializable
data class BillingDocumentDto(
    /** How the client addresses it. A processor's transaction id is not ours to put in a URL. */
    val invoiceNumber: String?,
    val amount: Money,
    val status: String,
    val billedAt: String? = null,
) {
    /** Refunds are their own negative rows, never a mutation of the original. */
    val isRefund: Boolean get() = amount.amountMinor < 0
}

/**
 * A plan price the server is prepared to put in front of a customer.
 *
 * The browser renders [pricePerSeat] and [interval] verbatim. It must not rebuild an offer from the
 * plan enum: availability is a product decision and money formatting belongs to the server.
 */
@Serializable
data class BillingPlanOfferDto(
    val plan: Plan,
    val interval: BillingInterval,
    val pricePerSeat: Money,
    val available: Boolean = false,
    val unavailableReason: String? = null,
)

/**
 * Everything `/app/settings/billing` renders.
 *
 * Assembled server-side from the database, so the page survives the processor being unreachable —
 * which is precisely when a customer is most likely to be looking at it.
 */
@Serializable
data class BillingOverviewDto(
    val plan: Plan,
    val status: PlanStatus,
    val interval: BillingInterval = BillingInterval.MONTH,
    val seats: SeatStateDto,
    val creditsUsed: Int = 0,
    val creditsIncluded: Int = 0,
    /** Granted on top of the plan's allowance, for this month only. Same reset, different origin. */
    val creditsGranted: Int = 0,
    /** When the subscription next bills, or when a cancelling one ends. Null on Free. */
    val renewsAt: String? = null,
    /** Non-null only while a payment is failing. */
    val dunning: DunningDto? = null,
    /** The monthly list price, before tax and currency conversion — labelled as such. */
    val listPricePerSeat: Money,
    val hasSubscription: Boolean = false,
    val checkoutAvailable: Boolean = false,
    /** Server-formatted offers. Empty keeps older cached/API payloads source compatible. */
    val planOffers: List<BillingPlanOfferDto> = emptyList(),
    /**
     * A checkout started and not yet confirmed.
     *
     * Drives the confirming page, and is why a customer who refreshes during the gap does not see
     * "Free" immediately after paying $19.
     */
    val pendingCheckoutStartedAt: String? = null,
    /**
     * True when this was assembled without reaching the processor.
     *
     * The page keeps its shape and adds a note. A Paddle-side failure must degrade to what the
     * database knows, never to a blank card — "we cannot read your plan" beside four real numbers is
     * usable; four empty boxes are not.
     */
    val degraded: Boolean = false,
) {
    val creditsRemaining: Int
        get() = (creditsIncluded + creditsGranted - creditsUsed).coerceAtLeast(0)
}

/** Why someone cancelled, asked at the moment they decide rather than in an email afterwards. */
@Serializable
data class CancelSubscriptionRequest(
    val reason: String? = null,
    val comment: String? = null,
)

/**
 * A short-lived link to the payment processor's own billing screens.
 *
 * Minted per request and never stored: the URL authenticates whoever holds it, so keeping one would
 * turn a link pasted into a support ticket into standing access to somebody's card details.
 */
@Serializable
data class BillingPortalDto(val url: String)
